<template>
  <div class="exp-card-wrapper">
    <div class="exp-card">
      <!-- User Info Header -->
      <div class="user-header">
        <div class="user-info">
          <div class="username">{{ displayUsername }}</div>
          <div class="tier-label" :class="tierClass">{{ tierName }}</div>
        </div>
        <div class="tier-avatar" :class="tierClass">
          {{ tierIcon }}
        </div>
      </div>

      <!-- Progress Bar Section -->
      <div class="progress-section">
        <div class="progress-bar-container">
          <div class="progress-bar-bg">
            <div
              class="progress-bar-fill"
              :style="{ width: progressPercent + '%' }"
            ></div>
            <div class="progress-bar-glow"></div>
          </div>
          <div class="progress-text">
            <span class="current-exp">{{
              formatNumber(currentExpDisplay)
            }}</span>
            <span class="separator">/</span>
            <span class="max-exp">{{ formatNumber(maxExpDisplay) }} EXP</span>
          </div>
        </div>
      </div>

      <!-- Tier Benefits (Expandable) -->
      <div class="tier-benefits" :class="{ expanded: isExpanded }">
        <div class="benefits-header" @click="isExpanded = !isExpanded">
          <span class="benefits-icon">⭐</span>
          <span class="benefits-title"
            >รางวัลเมื่อเลื่อนชั้นถัดไป [{{ nextTierName }}]</span
          >
          <span class="benefits-arrow">{{ isExpanded ? '▲' : '▼' }}</span>
        </div>
        <transition name="slide">
          <div v-if="isExpanded" class="benefits-content">
            <div
              v-for="(benefit, index) in nextTierBenefits"
              :key="index"
              class="benefit-item"
            >
              <span class="benefit-icon">{{ benefit.icon }}</span>
              <span class="benefit-text">{{ benefit.text }}</span>
            </div>
          </div>
        </transition>
      </div>

      <!-- How to Earn EXP -->
      <div class="earn-exp-section">
        <div class="earn-card">
          <span class="earn-icon">💰</span>
          <div class="earn-content">
            <div class="earn-title">ฝากเงิน</div>
            <div class="earn-value">฿100 = +10 EXP</div>
          </div>
        </div>
        <button
          class="claim-reward-btn"
          :class="{ disabled: !hasUnclaimedRewards }"
          :disabled="!hasUnclaimedRewards"
          @click="openClaimModal"
        >
          <span class="reward-icon">🎁</span>
          <span class="reward-text">รับรางวัล</span>
          <span v-if="hasUnclaimedRewards" class="reward-badge">{{
            unclaimedCount
          }}</span>
        </button>
      </div>
    </div>

    <!-- Claim Reward Modal -->
    <transition name="modal-fade">
      <div
        v-if="showClaimModal"
        class="claim-modal-overlay"
        @click.self="closeClaimModal"
      >
        <div class="claim-modal">
          <div class="claim-modal-header">
            <span class="modal-gift-icon">🎁</span>
            <h3 class="modal-title">รางวัลของคุณ</h3>
            <button class="modal-close-btn" @click="closeClaimModal">
              &times;
            </button>
          </div>
          <div class="claim-modal-body">
            <p class="modal-subtitle">
              คุณมี {{ unclaimedCount }} รางวัลรอรับ!
            </p>
            <!-- Loading State -->
            <div v-if="unclaimedRewardsLoading" class="rewards-loading">
              <div class="loading-spinner"></div>
              <span>กำลังโหลด...</span>
            </div>
            <!-- Rewards List -->
            <div v-else class="unclaimed-rewards-list">
              <div
                v-for="reward in unclaimedRewardsList"
                :key="reward.tier"
                class="unclaimed-reward-card"
              >
                <!-- Tier Header -->
                <div class="reward-card-header">
                  <span class="reward-tier-icon">{{ reward.tier_icon }}</span>
                  <div class="reward-tier-info">
                    <span class="reward-tier-name">{{ reward.tier_name }}</span>
                    <span class="reward-tier-name-en">{{
                      reward.tier_name_en
                    }}</span>
                  </div>
                </div>
                <!-- Benefits List -->
                <div class="reward-benefits">
                  <div
                    v-for="(benefit, idx) in reward.benefits"
                    :key="idx"
                    class="reward-benefit-item"
                  >
                    <span class="benefit-icon">{{ benefit.icon }}</span>
                    <span class="benefit-text">{{ benefit.text }}</span>
                  </div>
                </div>
                <!-- Totals Summary -->
                <div class="reward-totals">
                  <div v-if="reward.totals.credits > 0" class="total-item">
                    <span class="total-icon">💰</span>
                    <span class="total-value">{{
                      formatNumber(reward.totals.credits)
                    }}</span>
                  </div>
                  <div v-if="reward.totals.gems > 0" class="total-item">
                    <span class="total-icon">💎</span>
                    <span class="total-value">{{
                      formatNumber(reward.totals.gems)
                    }}</span>
                  </div>
                  <div
                    v-if="reward.totals.lottery_coupons > 0"
                    class="total-item"
                  >
                    <span class="total-icon">🎟️</span>
                    <span class="total-value">{{
                      formatNumber(reward.totals.lottery_coupons)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Fallback if no detailed rewards but has unclaimed -->
            <ul
              v-if="
                !unclaimedRewardsLoading && unclaimedRewardsList.length === 0
              "
              class="reward-list"
            >
              <li
                v-for="tier in unclaimedRewardTiers"
                :key="tier"
                class="reward-list-item"
              >
                <span class="reward-tier-icon">{{ getTierIcon(tier) }}</span>
                <span class="reward-tier-name">{{ getTierName(tier) }}</span>
              </li>
            </ul>
          </div>
          <div class="claim-modal-footer">
            <div v-if="claimError" class="claim-error-msg">
              {{ claimError }}
            </div>
            <div v-if="claimSuccess" class="claim-success-msg">
              รับรางวัลสำเร็จ!
            </div>
            <button
              v-if="!claimSuccess"
              class="claim-confirm-btn"
              :class="{ 'btn-loading': claiming }"
              :disabled="claiming"
              @click="confirmClaimReward"
            >
              <span v-if="claiming" class="loading-spinner-sm"></span>
              <span v-else>รับรางวัลทั้งหมด</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ExpCard',
  data() {
    return {
      tierInfo: null,
      loading: true,
      error: null,
      unclaimedRewards: [],
      unclaimedRewardsLoading: false,
      tiers: {
        cadet: {
          icon: '🎖️',
          name: 'ผู้สมัคร',
          nameEn: 'Cadet',
          minExp: 0
        },
        officer: {
          icon: '⚔️',
          name: 'นักรบ',
          nameEn: 'Officer',
          minExp: 500
        },
        platoon: {
          icon: '🛡️',
          name: 'หัวหน้าหมู่',
          nameEn: 'Platoon Leader',
          minExp: 2000
        },
        vice: {
          icon: '🦎',
          name: 'รองหัวหน้า',
          nameEn: 'Vice-Captain',
          minExp: 5000
        },
        captain: {
          icon: '🦖',
          name: 'หัวหน้ากอง',
          nameEn: 'Captain',
          minExp: 15000
        },
        kaiju8: {
          icon: '🐲',
          name: 'ไคจูหมายเลข 8',
          nameEn: 'Kaiju No. 8',
          minExp: 35000
        },
        director: {
          icon: '👑',
          name: 'ผู้บัญชาการ',
          nameEn: 'Director General',
          minExp: 75000
        }
      },
      tierOrder: [
        'cadet',
        'officer',
        'platoon',
        'vice',
        'captain',
        'kaiju8',
        'director'
      ],
      isExpanded: false,
      showClaimModal: false,
      claiming: false,
      claimSuccess: false,
      claimError: null
    }
  },
  computed: {
    displayUsername() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const phoneNo = userData.phone_no || ''
      const firstname = userData.firstname || ''
      return firstname ? `${phoneNo} - ${firstname}` : phoneNo || 'Player'
    },
    currentTier() {
      return this.tierInfo?.current_tier?.tier || 'cadet'
    },
    currentExpDisplay() {
      return this.tierInfo?.current_exp ?? 0
    },
    maxExpDisplay() {
      return this.tierInfo?.next_tier?.minExp ?? 100
    },
    progressPercent() {
      return this.tierInfo?.progress_percentage ?? 0
    },
    expToNextLevel() {
      return this.tierInfo?.exp_to_next_level ?? 0
    },
    tierIcon() {
      return this.tierInfo?.current_tier?.icon || this.tiers.cadet.icon
    },
    tierName() {
      return this.tierInfo?.current_tier?.name || this.tiers.cadet.name
    },
    tierClass() {
      return `tier-${this.currentTier}`
    },
    nextTierName() {
      if (this.tierInfo?.is_max_tier) {
        return this.tierName
      }
      return this.tierInfo?.next_tier?.name || this.tierName
    },
    nextTierBenefits() {
      return this.tierInfo?.next_tier_rewards?.benefits || []
    },
    hasUnclaimedRewards() {
      return (this.tierInfo?.total_unclaimed_count ?? 0) > 0
    },
    unclaimedCount() {
      return this.tierInfo?.total_unclaimed_count ?? 0
    },
    unclaimedRewardTiers() {
      return this.tierInfo?.unclaimed_reward_tiers || []
    },
    unclaimedRewardsList() {
      return this.unclaimedRewards
    }
  },
  mounted() {
    this.fetchTierInfo()
  },
  methods: {
    async fetchTierInfo() {
      try {
        this.loading = true
        const response = await this.$http.get('member/tier/info')
        if (response.data.success) {
          this.tierInfo = response.data.data
        }
      } catch (err) {
        this.error = err
        console.error('Failed to fetch tier info:', err)
      } finally {
        this.loading = false
      }
    },
    formatNumber(num) {
      return num.toLocaleString()
    },
    async fetchUnclaimedRewards() {
      try {
        this.unclaimedRewardsLoading = true
        const response = await this.$http.get('member/tier/unclaimed-rewards')
        if (response.data.success) {
          this.unclaimedRewards = response.data.data.unclaimed_rewards || []
        }
      } catch (err) {
        console.error('Failed to fetch unclaimed rewards:', err)
        this.unclaimedRewards = []
      } finally {
        this.unclaimedRewardsLoading = false
      }
    },
    async openClaimModal() {
      if (this.hasUnclaimedRewards) {
        this.showClaimModal = true
        await this.fetchUnclaimedRewards()
      }
    },
    closeClaimModal() {
      this.showClaimModal = false
      this.claimError = null
      this.claimSuccess = false
    },
    async confirmClaimReward() {
      this.claiming = true
      this.claimError = null
      this.claimSuccess = false

      try {
        const response = await this.$http.post('member/tier/claim-all-rewards')

        if (response.data.success) {
          this.claimSuccess = true
          await this.fetchTierInfo()
          this.$eventBus.$emit('refreshBalance')
          // Auto-close modal after showing success
          setTimeout(() => {
            this.closeClaimModal()
            this.claimSuccess = false
          }, 2000)
        } else {
          const errorMsg = response.data.message || 'ไม่สามารถรับรางวัลได้'
          this.claimError = errorMsg
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errorMsg,
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      } catch (err) {
        console.error('Failed to claim rewards:', err)
        const errorMsg =
          err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
        this.claimError = errorMsg
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMsg,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.claiming = false
      }
    },
    getTierIcon(tier) {
      return (this.tiers[tier] && this.tiers[tier].icon) || '🎖️'
    },
    getTierName(tier) {
      return (this.tiers[tier] && this.tiers[tier].name) || tier
    }
  }
}
</script>

<style lang="scss" scoped>
// Mobile-First styles (375px base)
.exp-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 16px;
  padding: 10px;
  margin: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    padding: 14px;
    margin: 0 20px;
    border-radius: 20px;
  }
}

// User Header
.user-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    gap: 16px;
    margin-bottom: 20px;
  }
}

.user-info {
  flex: 1;
  min-width: 0;
}

.username {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }
}

.tier-label {
  font-size: 0.9rem;
  font-weight: 500;

  &.tier-cadet {
    color: #a0a0a0;
  }

  &.tier-officer {
    color: #4ade80;
  }

  &.tier-platoon {
    color: #3b82f6;
  }

  &.tier-vice {
    color: #7367f0;
  }

  &.tier-captain {
    color: #f59e0b;
  }

  &.tier-kaiju8 {
    color: #ef4444;
  }

  &.tier-director {
    color: #fbbf24;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
  }
}

.tier-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;

  &.tier-cadet {
    border-color: #a0a0a0;
    box-shadow: 0 0 10px rgba(160, 160, 160, 0.3);
  }

  &.tier-officer {
    border-color: #4ade80;
    box-shadow: 0 0 10px rgba(74, 222, 128, 0.3);
  }

  &.tier-platoon {
    border-color: #3b82f6;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.3);
  }

  &.tier-vice {
    border-color: #7367f0;
    box-shadow: 0 0 10px rgba(115, 103, 240, 0.3);
  }

  &.tier-captain {
    border-color: #f59e0b;
    box-shadow: 0 0 10px rgba(245, 158, 11, 0.3);
  }

  &.tier-kaiju8 {
    border-color: #ef4444;
    box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
  }

  &.tier-director {
    border-color: #fbbf24;
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
  }

  @media (min-width: 768px) {
    width: 46px;
    height: 46px;
    font-size: 1.5rem;
  }
}

// Progress Bar
.progress-section {
  margin-bottom: 12px;

  @media (min-width: 768px) {
    margin-bottom: 16px;
  }
}

.progress-bar-container {
  position: relative;
}

.progress-bar-bg {
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    height: 16px;
    border-radius: 8px;
  }
}

.progress-bar-fill {
  height: 100%;
  background: var(--theme-gradient-primary);
  border-radius: 6px;
  transition: width 0.5s ease-out;
  position: relative;

  @media (min-width: 768px) {
    border-radius: 8px;
  }
}

.progress-bar-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.progress-text {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
  font-size: 0.75rem;

  .current-exp {
    color: var(--theme-primary);
    font-weight: 600;
  }

  .separator {
    color: rgba(255, 255, 255, 0.5);
  }

  .max-exp {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (min-width: 768px) {
    font-size: 0.9rem;
    margin-top: 8px;
  }
}

// Tier Benefits (Expandable)
.tier-benefits {
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;

  @media (min-width: 768px) {
    margin-bottom: 20px;
  }
}

.benefits-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:active {
    background: rgba(255, 255, 255, 0.08);
  }

  .benefits-icon {
    font-size: 1rem;
  }

  .benefits-title {
    flex: 1;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.85rem;
    font-weight: 500;
  }

  .benefits-arrow {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.7rem;
    transition: transform 0.3s ease;
  }

  @media (min-width: 768px) {
    padding: 12px 16px;

    .benefits-icon {
      font-size: 1.2rem;
    }

    .benefits-title {
      font-size: 0.9rem;
    }
  }
}

.benefits-content {
  padding: 0 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    padding: 0 16px 16px;
  }
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .benefit-icon {
    font-size: 1rem;
  }

  .benefit-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
  }

  @media (min-width: 768px) {
    padding: 10px 0;

    .benefit-icon {
      font-size: 1.1rem;
    }

    .benefit-text {
      font-size: 0.85rem;
    }
  }
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

// Earn EXP Section
.earn-exp-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (min-width: 768px) {
    gap: 15px;
  }
}

.earn-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);

  .earn-icon {
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .earn-content {
    min-width: 0;
  }

  .earn-title {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.65rem;
    margin-bottom: 2px;
  }

  .earn-value {
    color: #fff;
    font-size: 0.7rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: 768px) {
    padding: 12px 16px;
    gap: 12px;

    .earn-icon {
      font-size: 1.5rem;
    }

    .earn-title {
      font-size: 0.75rem;
    }

    .earn-value {
      font-size: 0.85rem;
    }
  }
}

// Claim Reward Button
.claim-reward-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #ffb6c1, #ffc0cb, #ffb6c1);
  border: 1px solid rgba(255, 182, 193, 0.5);
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255, 182, 193, 0.3);

  &:hover {
    background: linear-gradient(135deg, #ffc0cb, #ffd1dc, #ffc0cb);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 182, 193, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(255, 182, 193, 0.3);
  }

  .reward-icon {
    font-size: 1.2rem;
    animation: bounce 2s infinite;
  }

  .reward-text {
    color: #8b4557;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .reward-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: linear-gradient(135deg, #ff6b8a, #ff4d6d);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(255, 77, 109, 0.4);
    animation: pulse 2s infinite;
  }

  // Disabled state
  &.disabled {
    background: linear-gradient(135deg, #9ca3af, #d1d5db, #9ca3af);
    border-color: rgba(156, 163, 175, 0.5);
    box-shadow: none;
    cursor: not-allowed;

    &:hover {
      background: linear-gradient(135deg, #9ca3af, #d1d5db, #9ca3af);
      transform: none;
      box-shadow: none;
    }

    .reward-icon {
      animation: none;
      opacity: 0.6;
    }

    .reward-text {
      color: #6b7280;
    }
  }

  @media (min-width: 768px) {
    padding: 12px 16px;
    gap: 8px;

    .reward-icon {
      font-size: 1.4rem;
    }

    .reward-text {
      font-size: 0.85rem;
    }

    .reward-badge {
      top: -8px;
      right: -8px;
      min-width: 22px;
      height: 22px;
      font-size: 0.75rem;
      border-radius: 11px;
    }
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-2px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

// Claim Reward Modal
.claim-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
}

.claim-modal {
  background: linear-gradient(145deg, #1a1a2e, #16213e);
  border-radius: 20px;
  width: 100%;
  max-width: 340px;
  border: 1px solid rgba(255, 182, 193, 0.3);
  box-shadow: 0 10px 40px rgba(255, 182, 193, 0.2);
  overflow: hidden;
}

.claim-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 182, 193, 0.15),
    rgba(255, 192, 203, 0.1)
  );
  border-bottom: 1px solid rgba(255, 182, 193, 0.2);
  position: relative;

  .modal-gift-icon {
    font-size: 1.5rem;
  }

  .modal-title {
    flex: 1;
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
  }

  .modal-close-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover {
      color: #fff;
    }
  }
}

.claim-modal-body {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;

  .modal-subtitle {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    text-align: center;
    margin: 0 0 16px 0;
  }
}

// Loading State
.rewards-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 30px 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 182, 193, 0.3);
  border-top-color: #ffb6c1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Unclaimed Rewards List
.unclaimed-rewards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.unclaimed-reward-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 182, 193, 0.2);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 182, 193, 0.4);
    box-shadow: 0 4px 15px rgba(255, 182, 193, 0.15);
  }
}

.reward-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 182, 193, 0.1),
    rgba(255, 192, 203, 0.05)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .reward-tier-icon {
    font-size: 1.8rem;
  }

  .reward-tier-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .reward-tier-name {
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
  }

  .reward-tier-name-en {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.75rem;
  }
}

.reward-benefits {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reward-benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  .benefit-icon {
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  .benefit-text {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.85rem;
    font-weight: 500;
  }
}

.reward-totals {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 10px 16px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.total-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 182, 193, 0.15),
    rgba(255, 192, 203, 0.1)
  );
  border-radius: 20px;
  border: 1px solid rgba(255, 182, 193, 0.2);

  .total-icon {
    font-size: 0.9rem;
  }

  .total-value {
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
  }
}

.reward-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reward-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);

  .reward-tier-icon {
    font-size: 1.4rem;
  }

  .reward-tier-name {
    color: #fff;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.claim-modal-footer {
  padding: 16px 20px 20px;
}

.claim-error-msg {
  color: #ff6b6b;
  font-size: 0.85rem;
  text-align: center;
  margin-bottom: 10px;
}

.claim-success-msg {
  color: #4ade80;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  padding: 14px 0;
}

.claim-confirm-btn {
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #ffb6c1, #ff91a4);
  border: none;
  border-radius: 12px;
  color: #8b4557;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 182, 193, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: linear-gradient(135deg, #ffc0cb, #ff9fb0);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 182, 193, 0.5);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 10px rgba(255, 182, 193, 0.4);
  }

  &.btn-loading {
    opacity: 0.7;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.loading-spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(139, 69, 87, 0.3);
  border-top-color: #8b4557;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

// Modal transition
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .claim-modal,
.modal-fade-leave-active .claim-modal {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .claim-modal,
.modal-fade-leave-to .claim-modal {
  transform: scale(0.9) translateY(20px);
}
</style>
